import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface User {
  id: string | number;
  userName: string;
  email: string;
  roles: string[]; // Always an array for easier handling
  branches: string[]; // Storing branches
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  tenantId: string | null;
  branches: string[]; // Store branches at state level
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  refreshToken: null,
  loading: false,
  error: null,
  tenantId: null,
  branches: [],
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{ user: any; token: string; refreshToken: string }>
    ) => {
      state.isAuthenticated = true;
      const userData = action.payload.user;
      const roles = Array.isArray(userData.roles) ? userData.roles : [userData.roles];
      const branches = Array.isArray(userData.branches) ? userData.branches : [];
      state.user = { ...userData, roles, branches };
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.loading = false;
      state.branches = branches;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.branches = [];
    },
    saveAuthData: (state, action: PayloadAction<any>) => {
      const roles = Array.isArray(action.payload.roles) ? action.payload.roles : [action.payload.roles];
      const branches = Array.isArray(action.payload.branches) ? action.payload.branches : [];
      state.isAuthenticated = action.payload.isVerified;
      state.user = {
        id: action.payload.id,
        userName: action.payload.userName,
        email: action.payload.email,
        roles,
        branches,
      };
      state.token = action.payload.token || action.payload.jwToken;
      state.refreshToken = action.payload.refreshToken;
      state.tenantId = action.payload.tenantId;
      state.branches = branches;
    },
    resetAuthState: () => initialState,
  },
});

export const {
  loginRequest,
  loginSuccess,
  loginFailure,
  logout,
  saveAuthData,
  resetAuthState,
} = authSlice.actions;

export default authSlice.reducer;
